import { noop } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router";
import { Dimmer, Loader } from "semantic-ui-react";

import { ModalTypes, RestrictedResource } from "components";
import PublishConfirmationModal, {
  type EntityState,
} from "components/modal/PublishConfirmationModal/PublishConfirmationModal";
import RevertConfirmationModalTs from "components/modal/RevertConfirmationModalTs";
import { SwitchEntityTypes } from "enums/switchEntityTypes";
import {
  EmbeddedVideoDiscardSuccess,
  EmbeddedVideoPublishSuccess,
  LockEmbeddedVideoSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import AssociatedPacks from "features/Licensing/Packs/AssociatedPacks/AssociatedPacks";
import { NotifyStepConfig } from "features/SystemNotifications/types";
import { useObservable } from "hooks/useObservable";
import ModalManager from "utils/ModalManager";
import Segments from "../../../../components/navigation/segments/Segments";
import {
  PublishedStatusTypes,
  RolePermissions,
  RouteNames,
  TemplateTypes,
  UsersGroupsContext,
} from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import { bindAction, type EnumDictionary, type ISegment } from "../../../../interfaces";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import Configure from "../Details/Configure";
import SettingsTab from "../Details/SettingsTab";
import { reset as resetEmbeddedVideo } from "../state/EmbeddedVideoDetailsSlice";
import { resetTags } from "../state/EmbeddedVideosTagsSlice";
import { fetchEmbeddedVideo } from "../state/thunks/EmbeddedVideoDetailsThunk";
import { discardEmbeddedVideo, publishEmbeddedVideo } from "../state/thunks/EmbeddedVideosEntityStateThunk";
import EditEmbeddedVideoDetailsHeader from "./EditEmbeddedVideoDetailsHeader";
import EditEmbeddedVideoPeople from "./EditEmbeddedVideoPeople";
import EditEmbeddedVideoSubheaderContainer from "./EditEmbeddedVideoSubheaderContainer";
import EmbeddedVideoPerformance from "../Performance/EmbeddedVideoPerformance";

enum EditEmbeddedVideoSteps {
  Performance,
  Configuration,
  Settings,
  People,
  AssociatedPacks,
}

const pageIndexes = [
  EditEmbeddedVideoSteps.Performance,
  EditEmbeddedVideoSteps.Configuration,
  EditEmbeddedVideoSteps.Settings,
  EditEmbeddedVideoSteps.People,
  EditEmbeddedVideoSteps.AssociatedPacks,
];

const entityStateRtns = [EmbeddedVideoPublishSuccess, EmbeddedVideoDiscardSuccess, LockEmbeddedVideoSuccess];
const showLastModified = new Set([EditEmbeddedVideoSteps.Configuration]);
const showReportLastUpdated = new Set([EditEmbeddedVideoSteps.Performance]);
const overviewUrl = `/${RouteNames.contentEmbeddedVideos}`;

export type EditEmbeddedVideoPropsAll = PropsFromRedux;

export const EditEmbeddedVideo = (props: EditEmbeddedVideoPropsAll) => {
  const {
    fetchEmbeddedVideo,
    discardEmbeddedVideo,
    publishEmbeddedVideo,
    resetEmbeddedVideo,
    resetTags,
    embeddedVideo,
    userId,
    headerTitle,
  } = props;

  useEffect(() => {
    if (!props.isLoaded) {
      fetchEmbeddedVideo(embeddedVideoId);
    }

    return () => {
      resetEmbeddedVideo();
      resetTags();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPublishedAndNotPurchased = useMemo(
    () => !embeddedVideo.isDraft && !embeddedVideo.isPurchased,
    [embeddedVideo],
  );
  const publishedStatus = useMemo(
    () => PublishedStatusTypes.ConvertToPublishedStatusType(!embeddedVideo.isDraft, embeddedVideo.hasBeenPublished),
    [embeddedVideo],
  );

  const params = useParams();
  const embeddedVideoId = Number(params.id);

  const [wizardPages, { goToPage, goBack }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const publishObserver = useObservable<(onConfirm: () => void, entityState: EntityState) => void>();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [usersGroupsContext, setUsersGroupsContext] = useState(UsersGroupsContext.Groups);
  const revertModalManager = useMemo(
    () => new ModalManager(ModalTypes.RevertEmbeddedVideo, () => discardEmbeddedVideo(embeddedVideoId), false, userId),
    [discardEmbeddedVideo, userId, embeddedVideoId],
  );

  const pages: EnumDictionary<EditEmbeddedVideoSteps, ISegment> = useMemo(() => {
    return {
      [EditEmbeddedVideoSteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => {
          goToPage(EditEmbeddedVideoSteps.Performance);
        },
      },
      [EditEmbeddedVideoSteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => {
          goToPage(EditEmbeddedVideoSteps.Configuration);
        },
      },
      [EditEmbeddedVideoSteps.Settings]: {
        to: "settings",
        label: "Settings",
        onClick: () => {
          goToPage(EditEmbeddedVideoSteps.Settings);
        },
      },
      [EditEmbeddedVideoSteps.People]: {
        to: "people",
        label: "People",
        onClick: () => {
          goToPage(EditEmbeddedVideoSteps.People);
        },
      },
      [EditEmbeddedVideoSteps.AssociatedPacks]: {
        to: "packs",
        label: "Associated Packs",
        onClick: () => {
          goToPage(EditEmbeddedVideoSteps.AssociatedPacks);
        },
      },
    };
  }, [goToPage]);

  const [isValid, setIsValid] = useState(true);
  useLayoutEffect(() => {
    const handler = (_: EditEmbeddedVideoSteps, activeStepIndex: EditEmbeddedVideoSteps) => {
      pages[activeStepIndex].init?.();
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  const onEntiyStateChange = useCallback(() => {
    fetchEmbeddedVideo(embeddedVideoId);
  }, [fetchEmbeddedVideo, embeddedVideoId]);
  useRtn(entityStateRtns, onEntiyStateChange);

  const handlePublish = useCallback(() => {
    if (embeddedVideo.isDraft) {
      const entityState: EntityState = {
        itemsCount: embeddedVideo.settings.flows.length,
        entityId: embeddedVideo.id,
        templateType: embeddedVideo.hasBeenPublished
          ? TemplateTypes.EmbeddedVideoChanged
          : TemplateTypes.AddedToEmbeddedVideo,
        notifyConfigType: embeddedVideo.hasBeenPublished ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default,
        entityType: SwitchEntityTypes.EmbeddedVideo,
        hasBeenPublished: embeddedVideo.hasBeenPublished,
      };

      const cb = embeddedVideo.hasBeenPublished
        ? () => publishObserver.notify(noop, entityState)
        : () => publishEmbeddedVideo(entityState.entityId);
      cb();
    }
  }, [publishEmbeddedVideo, publishObserver, embeddedVideo]);

  const back = () => {
    if (!goBack()) {
      return overviewUrl;
    }
  };

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content scrollable-content">
          <EditEmbeddedVideoDetailsHeader
            embeddedVideoId={embeddedVideoId}
            overviewUrl={overviewUrl}
            title={headerTitle}
            canBeEdited={isPublishedAndNotPurchased}
            crudPermission={hasPermission}
            canBePublished={embeddedVideo.isDraft}
            isRevertVisible={embeddedVideo.hasBeenPublished}
            invalidFormDetails={!isValid}
            publishedStatus={publishedStatus}
            hideEntityStateButtons={activeTab === EditEmbeddedVideoSteps.People}
            entityStateActions={{
              onRevert: () => revertModalManager.execute(),
              onPublish: handlePublish,
            }}
            back={back}
            canAddPacks={isPublishedAndNotPurchased}
            showAddPacksButton={activeTab === EditEmbeddedVideoSteps.AssociatedPacks}
          />
          {embeddedVideo.hasBeenPublished && <RevertConfirmationModalTs modalManager={revertModalManager} />}
          <PublishConfirmationModal
            onTriggerPublishContentObserver={publishObserver}
            onConfirmAction={publishEmbeddedVideo}
            contentType="Embedded Video"
            dependencyType="flow"
          />
          <Dimmer active={props.isLoading} inverted>
            <Loader />
          </Dimmer>
          <EditEmbeddedVideoSubheaderContainer
            publishedStatus={publishedStatus}
            isUpdateInProgress={embeddedVideo.isDraft}
            lastModifiedDateTime={embeddedVideo.dateModified}
            showLastModified={showLastModified.has(activeTab)}
            showReportLastUpdated={showReportLastUpdated.has(activeTab)}
          >
            <Segments to={`${overviewUrl}/${embeddedVideoId}`}>
              <Segments.Segment {...pages[EditEmbeddedVideoSteps.Performance]} />
              <Segments.Segment {...pages[EditEmbeddedVideoSteps.Configuration]} />
              <Segments.Segment {...pages[EditEmbeddedVideoSteps.Settings]} />
              <Segments.Segment {...pages[EditEmbeddedVideoSteps.People]} />
              <Segments.Segment {...pages[EditEmbeddedVideoSteps.AssociatedPacks]} />
            </Segments>
          </EditEmbeddedVideoSubheaderContainer>
          <div className="scrollable-content">
            <Routes>
              <Route
                path={pages[EditEmbeddedVideoSteps.Performance].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <EmbeddedVideoPerformance
                      embeddedVideoTitle={embeddedVideo.title}
                      embeddedVideoId={embeddedVideoId}
                    />
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditEmbeddedVideoSteps.Configuration].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <Configure
                        entityId={embeddedVideoId}
                        acceptHandlers={wizardPages[EditEmbeddedVideoSteps.Configuration]}
                        disabled={!embeddedVideo.isDraft}
                        onIsValidChange={setIsValid}
                      />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditEmbeddedVideoSteps.Settings].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <SettingsTab acceptHandlers={wizardPages[EditEmbeddedVideoSteps.Settings]} />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditEmbeddedVideoSteps.People].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <EditEmbeddedVideoPeople
                      embeddedVideoId={embeddedVideoId}
                      acceptHandlers={wizardPages[EditEmbeddedVideoSteps.People]}
                      usersGroupsContext={usersGroupsContext}
                      onContextChanged={setUsersGroupsContext}
                    />
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditEmbeddedVideoSteps.AssociatedPacks].to}
                element={
                  <AssociatedPacks
                    acceptHandlers={wizardPages[EditEmbeddedVideoSteps.AssociatedPacks]}
                    contentType={"EmbeddedVideo"}
                    contentId={embeddedVideoId}
                    canAddPacks={isPublishedAndNotPurchased}
                    permissions={[RolePermissions.AssetsCreate]}
                  />
                }
              />
              <Route path="*" element={<Navigate to="../" replace />} />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const embeddedVideos = state.library.embeddedVideos;
  return {
    headerTitle: embeddedVideos.details.title,
    embeddedVideo: embeddedVideos.details.embeddedVideo,
    isLoading: embeddedVideos.details.isLoading,
    isLoaded: embeddedVideos.details.isLoaded,
    userId: Number(state.userProfile.id),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  discardEmbeddedVideo: bindAction(discardEmbeddedVideo, dispatch),
  fetchEmbeddedVideo: bindAction(fetchEmbeddedVideo, dispatch),
  publishEmbeddedVideo: bindAction(publishEmbeddedVideo, dispatch),
  resetEmbeddedVideo: bindAction(resetEmbeddedVideo, dispatch),
  resetTags: bindAction(resetTags, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditEmbeddedVideo);
export default ConnectedComponent;
